// src/utils/generateStudentHtml.js
import { formatJalaliLong, formatJalaliShort, toPersianDigits } from './date';

const safe = (v) => (v === null || v === undefined) ? '' : String(v);
const escapeHtml = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[c]));

// قالب‌بندی عدد/درصد به فارسی
const fmtNumber = (v) => {
  if (v === null || v === undefined || v === '') return '';
  // اگر عدد است یا قابل تبدیل به عدد است، نگه داریم فرمت عددی
  const n = Number(v);
  if (!Number.isFinite(n)) return toPersianDigits(String(v));
  // اگر اعشاری
  if (Math.abs(n) % 1 !== 0) return toPersianDigits(n.toFixed(2));
  return toPersianDigits(String(n));
};

// درصد با علامت درصد فارسی
const fmtPercent = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const cleaned = typeof v === 'string' && v.includes('%') ? v.replace('%', '') : v;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return toPersianDigits(String(v));
  return `${toPersianDigits(n.toFixed(2))} %`;
};

// قالب‌بندی تاریخ: تلاش برای استفاده از فیلدهای مختلف ورودی (iso/Date/string)
const fmtDateLong = (val) => {
  if (!val) return '';
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return escapeHtml(safe(val));
    return escapeHtml(formatJalaliLong(d));
  } catch {
    return escapeHtml(safe(val));
  }
};

const fmtDateShort = (val) => {
  if (!val) return '';
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return escapeHtml(safe(val));
    return escapeHtml(formatJalaliShort(d));
  } catch {
    return escapeHtml(safe(val));
  }
};

export function buildStudentReportHtml({ student, progress = [], answers = [], classRecords = [], generatedAt = new Date() }) {
  // رندر سطرهای پیشرفت
  const rowsProgress = (progress || []).map(p => {
    const dateInput = p.date ?? p.Date ?? p.DateString ?? p.Day ?? '';
    const total = p.total ?? p.Total ?? '';
    const correct = p.correct ?? p.Correct ?? '';
    const successRate = p.successRate ?? p.SuccessRate ?? '';
    return `<tr>
      <td>${fmtDateShort(dateInput)}</td>
      <td>${fmtNumber(total)}</td>
      <td>${fmtNumber(correct)}</td>
      <td>${fmtPercent(successRate)}</td>
    </tr>`;
  }).join('');

  // رندر سطرهای پاسخ‌ها
  const rowsAnswers = (answers || []).map(a => {
    const timeInput = a.answerTime ?? a.AnswerTime ?? a.AnswerTimeString ?? a.CreatedAt ?? '';
    const qText = a.questionText ?? a.QuestionContent ?? a.QuestionText ?? '';
    const selected = a.selectedOption ?? a.SelectedOption ?? '';
    const score = a.score ?? a.Score ?? '';
    return `<tr>
      <td>${fmtDateLong(timeInput)}</td>
      <td>${escapeHtml(safe(qText))}</td>
      <td style="text-align:center">${escapeHtml(safe(selected))}</td>
      <td style="text-align:center">${fmtNumber(score)}</td>
    </tr>`;
  }).join('');

  // رندر سطرهای رکورد کلاس
  const rowsClass = (classRecords || []).map(c => {
    const dateInput = c.date ?? c.Date ?? c.DateString ?? '';
    const type = c.type ?? c.Type ?? '';
    const desc = c.description ?? c.Description ?? '';
    const score = c.score ?? c.Score ?? '';
    // اگر عنوان درس در رکورد وجود داشته باشد نمایش بده
    const lessonTitle = c.lessonTitle ?? c.LessonTitle ?? (c.lesson && (c.lesson.title || c.lesson.Title)) ?? '';
    const lessonPart = lessonTitle ? ` <div style="color:#555;font-size:12px">درس: ${escapeHtml(safe(lessonTitle))}</div>` : '';
    return `<tr>
      <td>${fmtDateShort(dateInput)}</td>
      <td>${escapeHtml(safe(type))}${lessonPart}</td>
      <td>${escapeHtml(safe(desc))}</td>
      <td style="text-align:center">${fmtNumber(score)}</td>
    </tr>`;
  }).join('');

  const studentName = escapeHtml(safe(student?.name || ''));
  const studentCode = escapeHtml(safe(student?.code || ''));

  return `<!doctype html>
<html lang="fa">
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>گزارش دانش‌آموز - ${studentName}</title>
  <style>
    body{font-family:Tahoma,Arial,Helvetica,sans-serif;background:#fff;color:#111;padding:20px;direction:rtl}
    h1,h2{margin:8px 0}
    table{width:100%;border-collapse:collapse;margin:8px 0}
    th,td{border:1px solid #ddd;padding:8px;text-align:right;font-size:13px;vertical-align:top}
    th{background:#f3f6fb}
    .meta{color:#555;font-size:13px;margin-bottom:12px}
    .section{margin-top:18px}
    .small{font-size:12px;color:#666}
    .center{text-align:center}
  </style>
</head>
<body>
  <h1>گزارش تحصیلی</h1>
  <div class="meta">
    <div><strong>نام:</strong> ${studentName} &nbsp;&nbsp; <strong>کد:</strong> ${studentCode}</div>
    <div class="small"><strong>تاریخ تولید گزارش:</strong> ${escapeHtml(formatJalaliLong(new Date(generatedAt)))}</div>
  </div>

  <div class="section">
    <h2>خلاصه پیشرفت</h2>
    <table>
      <thead><tr><th>تاریخ</th><th>کل پاسخ</th><th>صحیح</th><th>درصد موفقیت</th></tr></thead>
      <tbody>${rowsProgress || '<tr><td colspan="4" class="center">بدون داده</td></tr>'}</tbody>
    </table>
  </div>

  <div class="section">
    <h2>تاریخچه پاسخ‌ها</h2>
    <table>
      <thead><tr><th>زمان</th><th>سؤال</th><th style="text-align:center">گزینه</th><th style="text-align:center">امتیاز</th></tr></thead>
      <tbody>${rowsAnswers || '<tr><td colspan="4" class="center">بدون داده</td></tr>'}</tbody>
    </table>
  </div>

  <div class="section">
    <h2>رکوردهای کلاس</h2>
    <table>
      <thead><tr><th>تاریخ</th><th>نوع</th><th>توضیح</th><th style="text-align:center">نمره</th></tr></thead>
      <tbody>${rowsClass || '<tr><td colspan="4" class="center">بدون رکورد</td></tr>'}</tbody>
    </table>
  </div>

  <footer style="margin-top:20px;color:#777;font-size:12px">تولیدشده توسط سامانه آموزشی</footer>
</body>
</html>`;
}
